import { useState, useRef } from "react";
import { Box } from "@chakra-ui/react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { useApp } from "src/components/App/Context";
import debug from "src/modules/debug-logger";
import Dialog from "src/components/Dialog";
import GenericFailureDialog from "src/components/GenericFailureDialog";
import lullabyFiles from "src/modules/lullaby/lullaby-files";
import useAudioRecorder from "src/hooks/useAudioRecorder";
import NavigationBar from "src/components/NavigationBar";
import { saveLullaby } from "src/modules/lullaby/duck/actions";
import {
  addRecordLullaby,
  updateRecordLullaby,
  saveAudioFile
} from "src/modules/lullaby/ajax/cloud";
import LullabiesEditorView from "./LullabiesEditorView";

import { v4 as uuid4 } from "uuid";
const MAX_ALLOWED_DURATION = 3 * 60 * 1000;

export default function LullabiesEditor({
  onBack,
  onClose,
  afterSave,
  originalLullaby
}) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const app = useApp();
  const [isSaving, setIsSaving] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [name, setName] = useState(originalLullaby?.name || "");
  const recordingUuidRef = useRef(uuid4());

  const audioRecorder = useAudioRecorder({
    getInitialRecordingSrc: originalLullaby?.audioFile
      ? () => lullabyFiles.srcResolver(null, originalLullaby)
      : null,
    maxAllowedDuration: MAX_ALLOWED_DURATION
  });

  function saveHandler() {
    setIsSaving(true);
    return originalLullaby ? updateHandler() : createHandler();
  }

  async function updateHandler() {
    try {
      let audioFile = originalLullaby.audioFile;
      const hasRecording = audioRecorder.audioData;
      if (hasRecording) {
        const { audioData } = audioRecorder;
        audioFile = await saveAudioFile(audioData);
        await lullabyFiles.write(audioFile.name, audioData);
      }
      const updatedLullaby = await updateRecordLullaby({
        lullabyId: originalLullaby.objectId,
        name,
        audioFile,
        duration: hasRecording
          ? await audioRecorder.getRecordingDuration()
          : originalLullaby.duration
      });
      dispatch(saveLullaby(updatedLullaby));
      afterSave();
      app.synchronizer.announceItemChange(
        app.synchronizer.SynchronizableItems.LULLABIES
      );
      lullabyFiles.delete(originalLullaby.audioFile.name);
    } catch (error) {
      debug.lullaby("Lullaby update failed.", error);
      setIsSaving(false);
      setHasError(true);
    }
  }

  async function createHandler() {
    try {
      const { audioData } = audioRecorder;
      const audioFile = await saveAudioFile(audioData);
      await lullabyFiles.write(audioFile.name, audioData);
      const createdLullaby = await addRecordLullaby({
        name,
        audioFile,
        uuid: recordingUuidRef.current,
        duration: await audioRecorder.getRecordingDuration()
      });
      dispatch(saveLullaby(createdLullaby));
      afterSave();
      app.synchronizer.announceItemChange(
        app.synchronizer.SynchronizableItems.LULLABIES
      );
    } catch (error) {
      debug.lullaby("Lullaby creation failed.", error);
      setIsSaving(false);
      setHasError(true);
    }
  }

  const canSave =
    name && audioRecorder.status === audioRecorder.Status.PAUSED_HAS_RECORDING;

  return (
    <>
      <Dialog
        isOpen={isSaving}
        isProcessing={true}
        processingText={t("savingChanges")}
      />
      <GenericFailureDialog
        isOpen={audioRecorder.hasPlayError}
        onDismiss={audioRecorder.clearPlayError}
      />
      <GenericFailureDialog
        isOpen={hasError}
        onDismiss={() => setHasError(false)}
      />
      <NavigationBar
        headingKey={
          originalLullaby ? "lullaby.editHeader" : "lullaby.newLullabyHeader"
        }
        actionButton={{
          onClick: saveHandler,
          disabled: !canSave,
          labelKey: "buttons.save"
        }}
        onBack={onBack}
        onClose={onClose}
      />
      <Box height="calc(100% - 61px)" p="24px">
        <LullabiesEditorView
          name={name}
          onNameChange={setName}
          audioRecorder={audioRecorder}
          duration={
            audioRecorder.audioData
              ? audioRecorder.getRecordingTime()
              : originalLullaby?.duration
          }
        />
      </Box>
    </>
  );
}
